import { appClient } from '@/lib/appwrite/appClient';
import { AppwritePayloadType, AppTablesTypeMap } from '@polybank/shared';
import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export default function useAppwriteTableQuery<K extends keyof AppTablesTypeMap>(
  collection: K,
  queries = [] as string[],
  queryOptions?: Omit<
    UseQueryOptions<{ rows: AppTablesTypeMap[K][]; total: number }>,
    'queryKey' | 'queryFn'
  >
) {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ['appwrite-table', collection, ...queries],
    [collection, queries]
  );
  const queryResult = useQuery({
    queryKey,
    queryFn: () => appClient.tables.typed.listRows(collection, [...queries]),
    ...queryOptions,
  });

  const onNewItem = useCallback(
    (item: AppTablesTypeMap[K]) => {
      queryClient.setQueryData(
        ['appwrite-table', collection, ...queries],
        (oldData: { rows: AppTablesTypeMap[K][]; total: number }) => {
          return {
            rows: [...oldData.rows, item],
            total: oldData.total + 1,
          };
        }
      );
    },
    [queryClient, collection, queries]
  );
  const onDataChange = useCallback(
    (data: { rows: AppTablesTypeMap[K][]; total: number }) => {
      queryClient.setQueryData<{
        rows: AppTablesTypeMap[K][];
        total: number;
      }>(['appwrite-table', collection, ...queries], (prev) => {
        return {
          rows: [...(prev?.rows ?? []), ...data.rows],
          total: (prev?.total ?? 0) + data.total,
        };
      });
      queryClient.refetchQueries({
        queryKey: ['appwrite-table', collection, ...queries],
      });
    },
    [queryClient, collection, queries]
  );

  const updateData = useCallback(
    (data: { rows: AppTablesTypeMap[K][]; total: number }) => {
      queryClient
        .getQueryCache()
        .findAll({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'appwrite-table' &&
            q.queryKey[1] === collection,
        })
        .forEach((query) => {
          queryClient.setQueryData(
            query.queryKey,
            (oldData: { rows: AppTablesTypeMap[K][]; total: number }) => {
              return {
                rows: [...oldData.rows, ...data.rows],
                total: oldData.total + data.total,
              };
            }
          );
        });
    },
    [queryClient, collection]
  );

  const removeItem = useCallback(
    (itemId: string) => {
      // Remove item from all queries that match "appwrite-table-" and the collection name in the query key
      queryClient
        .getQueryCache()
        .findAll({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'appwrite-table' &&
            q.queryKey[1] === collection,
        })
        .forEach((query) => {
          queryClient.setQueryData(
            query.queryKey,
            (oldData: { rows: AppTablesTypeMap[K][]; total: number }) => {
              if (!oldData) return oldData;
              return {
                rows: oldData.rows.filter((row) => row.$id !== itemId),
                total: oldData.total - 1,
              };
            }
          );
        });
    },
    [queryClient, collection]
  );
  const addItem = useCallback(
    (item: AppwritePayloadType<AppTablesTypeMap[K]>) => {
      queryClient.setQueryData(
        queryKey,
        (oldData: { rows: AppTablesTypeMap[K][]; total: number }) => {
          return {
            rows: [...oldData.rows, item],
            total: oldData.total + 1,
          };
        }
      );
    },
    [queryClient, queryKey]
  );

  const updateItem = useCallback(
    (itemId: string, item: Partial<AppTablesTypeMap[K]>) => {
      queryClient
        .getQueryCache()
        .findAll({
          predicate: (q) =>
            Array.isArray(q.queryKey) &&
            q.queryKey[0] === 'appwrite-table' &&
            q.queryKey[1] === collection,
        })
        .forEach((query) => {
          queryClient.setQueryData(
            query.queryKey,
            (oldData: { rows: AppTablesTypeMap[K][]; total: number }) => {
              return {
                rows: oldData.rows.map((row) =>
                  row.$id === itemId ? { ...row, ...item } : row
                ),
                total: oldData.total,
              };
            }
          );
        });
    },
    [queryClient, collection]
  );

  const mappedRows = useMemo(() => {
    return (
      queryResult.data?.rows.reduce(
        (acc, row) => {
          acc[row.$id!] = row;
          return acc;
        },
        {} as Record<string, AppTablesTypeMap[K]>
      ) ?? {}
    );
  }, [queryResult.data]);

  const addOrUpdateItem = useCallback(
    (item: Partial<AppTablesTypeMap[K]>) => {
      if (!mappedRows[item.$id!]) {
        addItem(item);
      } else {
        updateItem(item.$id!, item);
      }
    },
    [mappedRows, addItem, updateItem]
  );
  return useMemo(
    () => ({
      ...queryResult,
      data: queryResult.data ?? { rows: [], total: 0 },
      onDataChange,
      onNewItem,
      updateData,
      removeItem,
      addItem,
      updateItem,
      mappedRows,
      addOrUpdateItem,
    }),
    [
      queryResult,
      onDataChange,
      onNewItem,
      updateData,
      removeItem,
      addItem,
      updateItem,
      mappedRows,
      addOrUpdateItem,
    ]
  );
}
